/**
 * Hook for toggling likes in list views with optimistic cache updates
 * 
 * Uses the same pattern as useToggleLike (detail page) - local state for immediate feedback,
 * then invalidates queries to refetch from backend (ERPNext is source of truth).
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import { erp } from "@/lib/erp";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { parseLikedBy } from "../utils/parseLikedBy";
import { getCandidateLikeUserIds } from "../utils/likeUserIds";

type UseToggleLikeInListParams = {
  doctype: string;
};

type ToggleLikeParams = {
  doctype: string;
  name: string;
  nextLiked: boolean;
};

/**
 * Hook for toggling likes in list views
 * 
 * Matches the pattern from useToggleLike (detail page sidebar) which works correctly.
 * Uses local optimistic update pattern, then invalidates to refetch from ERPNext backend.
 */
export function useToggleLikeInList({ doctype }: UseToggleLikeInListParams) {
  const { data: currentUser } = useCurrentUser();
  const queryClient = useQueryClient();

  // Candidate identifiers that may appear in Frappe's `_liked_by` list.
  // (Commonly email, sometimes username.)
  const candidateUserIds = getCandidateLikeUserIds(currentUser);

  const preferredUserId = (currentUser?.email || currentUser?.name || "").trim() || null;

  const mutation = useMutation({
    mutationFn: async ({ doctype, name, nextLiked }: ToggleLikeParams) => {
      // Call backend to toggle like (same as detail page)
      await erp.docs.toggleLike({
        doctype,
        name,
        add: nextLiked,
      });
    },
    onMutate: async ({ name, nextLiked }) => {
      if (!preferredUserId || candidateUserIds.length === 0) {
        return { previous: [] as Array<[QueryKey, unknown]> };
      }

      const predicate = (q: { queryKey: unknown }) => {
        const key = q.queryKey as unknown[];
        return (
          Array.isArray(key) &&
          (key[0] === "doctype-list" || key[0] === "list") &&
          key[1] === doctype
        );
      };

      const previous = queryClient.getQueriesData<unknown>({ predicate });

      const patchLikedByRaw = (likedByRaw: unknown): unknown => {
        const likedBy = parseLikedBy(likedByRaw);

        if (nextLiked) {
          // When liking: prefer the identifier we have (usually email), but avoid duplicates.
          if (!likedBy.includes(preferredUserId)) {
            likedBy.push(preferredUserId);
          }
        } else {
          // When unliking: remove any identifier that could represent the current user.
          for (const id of candidateUserIds) {
            let idx = likedBy.indexOf(id);
            while (idx !== -1) {
              likedBy.splice(idx, 1);
              idx = likedBy.indexOf(id);
            }
          }
        }

        if (Array.isArray(likedByRaw)) return likedBy;
        // Frappe stores `_liked_by` as JSON string; keep that contract by default.
        return JSON.stringify(likedBy);
      };

      const patchItem = (item: unknown): [unknown, boolean] => {
        if (!item || typeof item !== "object") return [item, false];
        const rec = item as Record<string, unknown>;

        let changed = false;
        let nextRec: Record<string, unknown> = rec;

        if (rec.name === name) {
          const nextLikedBy = patchLikedByRaw(rec._liked_by);
          if (nextLikedBy !== rec._liked_by) {
            nextRec = { ...nextRec, _liked_by: nextLikedBy };
            changed = true;
          }
        }

        const variants = rec.variants;
        if (Array.isArray(variants)) {
          let anyVariantChanged = false;
          const nextVariants = variants.map((v) => {
            const [nv, c] = patchItem(v);
            if (c) anyVariantChanged = true;
            return nv;
          });
          if (anyVariantChanged) {
            nextRec = { ...nextRec, variants: nextVariants };
            changed = true;
          }
        }

        return [changed ? nextRec : rec, changed];
      };

      const patchResponse = (old: unknown): unknown => {
        if (!old || typeof old !== "object") return old;
        const obj = old as Record<string, unknown>;
        const data = obj.data;
        if (!Array.isArray(data)) return old;

        let anyChanged = false;
        const nextData = data.map((it) => {
          const [ni, c] = patchItem(it);
          if (c) anyChanged = true;
          return ni;
        });

        if (!anyChanged) return old;
        return { ...obj, data: nextData };
      };

      queryClient.setQueriesData({ predicate }, patchResponse);

      return { previous };
    },
    onSuccess: () => {
      // Invalidate queries to refetch from backend (ERPNext state is source of truth)
      // Same pattern as useToggleLike - invalidate list and sidebar queries
      queryClient.invalidateQueries({ queryKey: ["doctype-list", doctype] });
      queryClient.invalidateQueries({ queryKey: ["doc-sidebar", doctype] });
      // Also invalidate generic list queries
      queryClient.invalidateQueries({ queryKey: ["list", doctype] });
    },
    onError: (_err, _vars, ctx) => {
      // Roll back optimistic cache updates if we applied them
      const previous = (ctx as { previous?: Array<[QueryKey, unknown]> } | undefined)?.previous;
      if (previous?.length) {
        for (const [key, data] of previous) {
          queryClient.setQueryData(key, data);
        }
      }
      // Error is handled by React Query's error state
      // UI can display error via mutation.error if needed
      // No console.error needed - errors should be user-visible via toast/notification
    },
  });

  return {
    toggleLikeAsync: (params: { name: string; nextLiked: boolean }) => {
      if (!preferredUserId) {
        return Promise.resolve();
      }
      return mutation.mutateAsync({
        doctype,
        name: params.name,
        nextLiked: params.nextLiked,
      });
    },
    toggleLike: (params: { name: string; nextLiked: boolean }) => {
      if (!preferredUserId) {
        // Silently return - user not logged in is expected state, not an error
        return;
      }
      mutation.mutate({
        doctype,
        name: params.name,
        nextLiked: params.nextLiked,
      });
    },
    isLoading: mutation.isPending,
    error: mutation.error,
  };
}

